"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { Loader2 } from "lucide-react"

const PAGE_SIZE = 12
const VISIBLE_KEY = "igrid_visible:"
const SCROLL_KEY = "igrid_scroll:"

interface InfiniteProductGridProps {
  children: React.ReactNode
  totalCount: number
}

/**
 * Wraps server-rendered product cards and reveals them progressively on scroll.
 * Uses IntersectionObserver on a sentinel element near the bottom.
 *
 * Returning from a product page restores how many cards were revealed and the
 * scroll offset (persisted per pathname+query in sessionStorage), so you land
 * where you left off instead of at page 1, top. Works for SPA <Link> back
 * navigation too (App Router remounts this component and resets local state).
 */
export function InfiniteProductGrid({
  children,
  totalCount,
}: InfiniteProductGridProps) {
  const allItems = React.Children.toArray(children)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const key = `${pathname}?${searchParams.toString()}`
  const visibleStorageKey = VISIBLE_KEY + key
  const scrollStorageKey = SCROLL_KEY + key

  // Restore revealed count from a prior visit to this exact list (same
  // pathname+query). sessionStorage is empty on a fresh load and the key
  // changes when filters change, so this only fires on genuine return.
  const [visibleCount, setVisibleCount] = useState<number>(() => {
    if (typeof window === "undefined") return PAGE_SIZE
    const saved = sessionStorage.getItem(VISIBLE_KEY + key)
    return saved ? Math.min(parseInt(saved, 10) || PAGE_SIZE, allItems.length) : PAGE_SIZE
  })
  const sentinelRef = useRef<HTMLDivElement>(null)
  const didRestoreScroll = useRef(false)
  const hasMore = visibleCount < allItems.length

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, allItems.length))
  }, [allItems.length])

  // Persist how many cards are revealed.
  useEffect(() => {
    sessionStorage.setItem(visibleStorageKey, String(visibleCount))
  }, [visibleCount, visibleStorageKey])

  // Continuously persist the scroll offset (rAF-throttled). Saving only on
  // unmount/beforeunload was too late: Next resets scroll on navigation, so
  // the captured value was 0. Tracking live keeps the last real position.
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        sessionStorage.setItem(scrollStorageKey, String(window.scrollY))
      })
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [scrollStorageKey])

  // Restore scroll once on mount, after the revealed cards have rendered (so
  // the page is tall enough to reach the saved offset).
  useEffect(() => {
    if (didRestoreScroll.current) return
    didRestoreScroll.current = true
    const saved = sessionStorage.getItem(scrollStorageKey)
    const y = saved ? parseInt(saved, 10) : 0
    if (!y || !Number.isFinite(y)) return
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        window.scrollTo({ top: y, behavior: "instant" as ScrollBehavior })
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { rootMargin: "400px" }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  const visibleItems = allItems.slice(0, visibleCount)

  return (
    <>
      <ul
        className="grid grid-cols-1 w-full sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        data-testid="products-list"
      >
        {visibleItems}
      </ul>

      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </>
  )
}
